import * as csstree from 'css-tree';

/**
 * Rewrites CSS `body` type selectors in a selector string to the given Open Canvas
 * body element target selector.
 *
 * Uses css-tree to parse and walk TypeSelector nodes, replacing only those with
 * name `body`. Avoids false positives like `.body`, `#body`, `[data-tag="body"]`,
 * or `body-wrapper`.
 *
 * Returns the rewritten selector string, or `null` if the selector text is not
 * valid CSS.
 */
export function rewriteBodySelectors(
  selectorText: string,
  bodyElementStyleTarget: string
): string | null {
  // Quick check: avoid parse overhead when no word-boundary `body` token exists.
  // May produce false positives (e.g. `body-foo`, `.body`) but never false negatives.
  if (!/\bbody\b/i.test(selectorText)) return selectorText;

  let ast: csstree.CssNode;
  try {
    ast = csstree.parse(selectorText, {
      context: 'selectorList',
      onParseError(error) {
        throw error;
      },
    });
  } catch {
    return null;
  }

  // Parse the target selector once; clone for each replacement so each
  // substitution gets independent AST nodes.
  let targetSelector: csstree.Selector;
  try {
    const targetList = csstree.parse(bodyElementStyleTarget, {
      context: 'selectorList',
    });
    if (targetList.type !== 'SelectorList') return null;
    const first = targetList.children.first;
    if (!first || first.type !== 'Selector') return null;
    targetSelector = first;
  } catch {
    return null;
  }

  let modified = false;

  csstree.walk(ast, {
    visit: 'TypeSelector',
    enter(node, item, list) {
      if (node.name.toLowerCase() !== 'body') return;
      const clonedTarget = csstree.clone(targetSelector);
      if (clonedTarget.type !== 'Selector') return;
      list.replace(item, clonedTarget.children);
      modified = true;
    },
  });

  return modified ? csstree.generate(ast) : selectorText;
}
